/* eslint-disable react-refresh/only-export-components */
/**
 * Screen Reader Context Provider
 * Handles screen reader announcements and live regions
 */

import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { getScreenReader } from '@/lib/enhanced-screen-reader';

interface ScreenReaderContextType {
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  isScreenReaderMode: boolean;
  enableScreenReaderMode: () => void;
  disableScreenReaderMode: () => void;
}

const ScreenReaderContext = createContext<ScreenReaderContextType | undefined>(undefined);

export const useScreenReader = () => {
  const context = useContext(ScreenReaderContext);
  if (context === undefined) {
    throw new Error('useScreenReader must be used within a ScreenReaderProvider');
  }
  return context;
};

interface ScreenReaderProviderProps {
  children: React.ReactNode;
}

export const ScreenReaderProvider: React.FC<ScreenReaderProviderProps> = ({ children }) => {
  const [isScreenReaderMode, setIsScreenReaderMode] = useState(false);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const enhancedScreenReaderRef = useRef<ReturnType<typeof import('@/lib/enhanced-screen-reader').getScreenReader> | null>(null);

  // Initialize enhanced screen reader and detect screen reader usage
  useEffect(() => {
    // Initialize enhanced screen reader
    enhancedScreenReaderRef.current = getScreenReader({
      enableLogging: process.env.NODE_ENV === 'development',
      respectUserPreferences: true,
    });

    // Detect if screen reader is active
    setIsScreenReaderMode(enhancedScreenReaderRef.current.isActive());

    // Create fallback live region for compatibility
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.id = 'accessibility-live-region';
    document.body.appendChild(liveRegion);
    liveRegionRef.current = liveRegion;

    return () => {
      if (liveRegionRef.current && document.body.contains(liveRegionRef.current)) {
        document.body.removeChild(liveRegionRef.current);
      }
    };
  }, []);

  const announceToScreenReader = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    // Use enhanced screen reader if available
    if (enhancedScreenReaderRef.current) {
      enhancedScreenReaderRef.current.announce(message, priority);
    } else if (liveRegionRef.current) {
      // Fallback to basic live region
      liveRegionRef.current.setAttribute('aria-live', priority);
      liveRegionRef.current.textContent = message;
      
      // Clear the message after a short delay to allow for repeated announcements
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = '';
        }
      }, 1000);
    }
  }, []);

  const enableScreenReaderMode = useCallback(() => {
    setIsScreenReaderMode(true);
  }, []);

  const disableScreenReaderMode = useCallback(() => {
    setIsScreenReaderMode(false);
  }, []);

  const contextValue: ScreenReaderContextType = {
    announceToScreenReader,
    isScreenReaderMode,
    enableScreenReaderMode,
    disableScreenReaderMode,
  };

  return (
    <ScreenReaderContext.Provider value={contextValue}>
      {children}
    </ScreenReaderContext.Provider>
  );
};